import React, { useEffect } from 'react';
import { Box, useColorModeValue } from '@chakra-ui/react';
import UnifiedCaptions from './UnifiedCaptions';
import { useCaptions } from '../context/CaptionsContext';
import { useAudio } from '../context/AudioContext';

interface SlideContainerProps {
  children: React.ReactNode;
  captionsText?: string;
  isSpeaking?: boolean;
  hudStyle?: 'dark' | 'light';
  showCaptions?: boolean;
  audioUrl?: string | null;
}

/**
 * A container component that wraps slide content and captions in a unified container
 * with consistent styling and behavior across all slides.
 */
const SlideContainer: React.FC<SlideContainerProps> = ({
  children,
  captionsText = '',
  isSpeaking = false,
  hudStyle = 'dark',
  showCaptions = true,
  audioUrl = null
}) => {
  // Create a scrollbar style that's visible but not intrusive
  const scrollbarColor = useColorModeValue('rgba(0,0,0,0.3)', 'rgba(255,255,255,0.3)');
  const scrollbarBg = useColorModeValue('rgba(0,0,0,0.05)', 'rgba(255,255,255,0.05)');

  // Get captions context
  const { showCaptions: showCaptionsContext } = useCaptions();
  const shouldShowCaptions = showCaptions && showCaptionsContext;

  // Initialize audio context for playback
  const { playAudioForSlide } = useAudio();

  // Audio playback is now handled by StakeholderDeck
  useEffect(() => {
    console.log('[SlideContainer] Audio playback now handled by StakeholderDeck');
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="calc(100vh - 160px)"
      position="relative"
    >
      {/* Main content with scrolling */}
      <Box
        flex="1"
        overflowY="auto"
        pr={2}
        css={{
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: scrollbarBg,
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: scrollbarColor,
            borderRadius: '10px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(0,0,0,0.5)',
          },
          // Firefox scrollbar styles
          scrollbarWidth: 'thin',
          scrollbarColor: `${scrollbarColor} ${scrollbarBg}`,
        }}
      >
        {children}
      </Box>
      
      {/* Unified Captions Component */}
      {shouldShowCaptions && (
        <UnifiedCaptions 
          text={captionsText} 
          isSpeaking={isSpeaking} 
          hudStyle={hudStyle} 
        />
      )}
    </Box>
  );
};

export default SlideContainer;
